// Кои продукти са минали от едната маса на другата.
//
// Отговорът на `GET /bills/splits` носи цялото `items` на всяко разделяне, но
// старият екран показваше само БРОЯ им — самите редове не се виждаха никъде.
// Модалът е единственото място с тях; заявка не се прави, данните вече са
// заредени със справката.
//
// Имената на продуктите идват от САМИЯ отговор (`item_names` = {език: име}),
// не от менюто: така редът се чете и когато продуктът вече е изтрит оттам.
// `modifier_id` е двузначно поле — числов id на модификатор ИЛИ свободен
// текст на коментар (виж `OrderItemRow`), затова се разделят по същия признак;
// модификаторите са единственото, за което менюто още трябва.

import { ArrowRightOutlined, CloseOutlined, SwapOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../../../hooks/useViewport";
import { useMenuModifiers } from "../../../../hooks/useMenu";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import { fmtEuro } from "../../../../utils/currency";
import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import {
  formatQuantity,
  itemName,
} from "../billOperations/billOperationsFormat";
import type { BillSplitItem } from "../../../../api/reportsBillsSplits";
import type { SplitRow } from "./billsSplitsMath";

type Props = {
  /** `null` държи модала затворен. */
  split: SplitRow | null;
  onClose: () => void;
};

export const BillSplitItemsModal = ({ split, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const modifiers = useMenuModifiers();
  const langCode = i18n.language?.startsWith("en") ? "en" : "bg";

  /** `null` = стойността НЕ е познат модификатор, значи е коментар. */
  const modifierName = useMemo(() => {
    const namesById = new Map<string, string>();
    for (const modifier of modifiers) {
      const translated =
        modifier.translations.find(
          (translation) => translation.languageCode === langCode,
        )?.name ?? modifier.translations[0]?.name;
      namesById.set(String(modifier.id), translated ?? modifier.slug);
    }
    return (id: string) => namesById.get(id) ?? null;
  }, [modifiers, langCode]);

  // Клас „Wide" от референцията на модалите — списък с продукти и суми.
  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const moment = split ? dayjs(split.split_date) : null;

  return (
    <Modal
      open={split !== null}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="flex max-h-[82vh] flex-col bg-canvas">
        <header
          className={`flex shrink-0 items-start gap-3 border-b border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-5"
          }`}
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-[10px] bg-brand-tint">
            <SwapOutlined className="text-[20px] text-brand-deep-ink" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 flex flex-wrap items-center gap-1.5 text-[16px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              <span className="rounded-md bg-brand-tint px-2 py-[3px] text-[13px] leading-[normal] text-brand-deep-ink">
                {split?.from_bill_name}
              </span>
              <ArrowRightOutlined aria-hidden className="text-[12px] text-muted" />
              <span className="rounded-md bg-brand-tint px-2 py-[3px] text-[13px] leading-[normal] text-brand-deep-ink">
                {split?.to_bill_name}
              </span>
            </h2>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] font-medium leading-[1.4] text-soft">
              <span className="font-pos-mono tabular-nums">
                {moment ? moment.format("DD.MM.YYYY HH:mm") : ""}
              </span>
              <span aria-hidden>·</span>
              <span className="truncate">{split?.waiterLabel || EM_DASH}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid size-[30px] shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </header>

        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
            isNarrow ? "px-3 py-3" : "px-[18px] py-4"
          }`}
        >
          {split?.comment ? (
            <p className="mb-2 rounded-xl border border-shade-2 bg-surface px-3.5 py-2.5 text-[12.5px] font-medium leading-[1.4] text-soft">
              <span className="font-pos-mono text-[10px] font-bold uppercase tracking-[0.1em] text-muted">
                {`${t("reportsPage.billsSplitsComment")} · `}
              </span>
              {split.comment}
            </p>
          ) : null}

          {!split || split.items.length === 0 ? (
            <p className="py-10 text-center text-[13px] font-medium text-soft">
              {t("reportsPage.billsSplitsNoItems")}
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {split.items.map((item) => (
                <li key={item.id}>
                  <SplitItemRow
                    item={item}
                    language={langCode}
                    modifierName={modifierName}
                    isNarrow={isNarrow}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer
          className={`flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-4"
          }`}
        >
          <div className="flex min-w-0 flex-col">
            <span className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
              {t("reportsPage.transferredProducts")}
            </span>
            <span className="mt-1 font-pos-mono text-[14px] font-bold leading-none tabular-nums text-ink">
              {split?.items.length ?? 0}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="font-pos-mono text-[18px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-ink">
              {fmtEuro(split?.amount ?? 0)}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-[10px] border border-shade-3 bg-surface px-4 text-[13px] font-bold text-soft transition-colors hover:bg-fill"
            >
              {t("common.close")}
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

/* ────────────────────────────── един продукт ────────────────────────────── */

type RowProps = {
  item: BillSplitItem;
  language: string;
  modifierName: (id: string) => string | null;
  isNarrow: boolean;
};

const SplitItemRow = ({
  item,
  language,
  modifierName,
  isNarrow,
}: RowProps) => {
  // Количествата минават през общото `formatQuantity` (цели без нули след
  // запетаята, дробните с три знака) — както в „История на операциите".
  const quantity = formatQuantity(item.quantity);
  const price = parseDecimal(item.price);

  // Истинските модификатори се изписват като списък, а свободният текст —
  // като коментар на реда. Разделя ги това дали id-то е познат модификатор.
  const modifierLabels: string[] = [];
  const comments: string[] = [];
  for (const modifier of item.modifiers) {
    const name = modifierName(modifier.modifier_id);
    if (name) modifierLabels.push(name);
    else if (modifier.modifier_id) comments.push(modifier.modifier_id);
  }

  return (
    <article
      className={`flex flex-col gap-1.5 rounded-xl border border-shade-2 bg-surface ${
        isNarrow ? "px-3.5 py-3" : "px-4 py-3"
      }`}
    >
      <div className="flex items-start justify-between gap-2.5">
        <span className="min-w-0 flex-1 text-[13.5px] font-bold leading-[1.25] text-ink">
          {itemName(item.item_names, language)}
        </span>
        <span className="shrink-0 font-pos-mono text-[14px] font-bold leading-none tabular-nums text-ink">
          {fmtEuro(parseDecimal(item.total_sum))}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-muted">
        <span className="font-pos-mono tabular-nums">
          {`${quantity} × ${fmtEuro(price)}`}
        </span>
        {item.status_name ? (
          <>
            <span aria-hidden>·</span>
            <span>{item.status_name}</span>
          </>
        ) : null}
      </div>

      {modifierLabels.length ? (
        <p className="m-0 text-[12px] font-medium leading-[1.35] text-soft">
          {modifierLabels.join(" · ")}
        </p>
      ) : null}

      {comments.map((comment, index) => (
        <p
          key={`comment-${index}`}
          className="m-0 text-[12px] font-medium leading-[1.35] text-brand-deep-ink"
        >
          {comment}
        </p>
      ))}

      {item.additions.length ? (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {item.additions.map((addition) => (
            <li
              key={addition.id}
              className="flex items-center justify-between gap-2 text-[12px] font-medium text-soft"
            >
              <span className="min-w-0 truncate">
                {`+ ${itemName(addition.item_names, language)}`}
                <span className="font-pos-mono tabular-nums text-muted">
                  {` × ${formatQuantity(addition.quantity)}`}
                </span>
              </span>
              <span className="shrink-0 font-pos-mono tabular-nums text-muted">
                {fmtEuro(parseDecimal(addition.total_sum))}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
};
